import { Input, Button } from "@afterpaytouch/core";
import { useTranslation } from "next-i18next";
import React, {
    useState,
    useEffect,
    ChangeEvent,
    KeyboardEvent,
    useMemo,
    SyntheticEvent,
} from "react";

import { CATEGORY_LOCATIONS_CONTEXT } from "@/shared/constants";
import { distanceBetweenPositions } from "@/shared/helpers/locations-helper";

import { localeCountryCodeMap } from "@/translations/config";

import { getGeocodedLocations } from "@/pages/api/store-directory/locations";

import { useCurrentLocale } from "@/context/LanguageContext";

import { BreadCrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { PAGE_TYPES } from "@/components/breadcrumbs/breadcrumbs.constants";
import Grid from "@/components/layout/grid";
import Loader from "@/components/loaders/loader";

import { useIsArcade } from "@/hooks/useIsArcade";
import { useCategoryLocations, useStoreLocations } from "@/hooks/useLocations";
import { usePosition } from "@/hooks/usePosition";

import { CategoryProps } from "@/interfaces/category-interface";
import {
    GeocodedResultsProps,
    LatLngLiteralProps,
    LocationProps,
} from "@/interfaces/locations/location-interface";
import { StoreProps as SingleStoreProps } from "@/interfaces/stores-interface";

import { Locale } from "@/types/locale";

import { InStoreList } from "./in-store-list";
import { InStoreMap } from "./in-store-map";
import { InStoreNoLocations } from "./in-store-no-locations";
import styles from "./instore.module.scss";

export interface InStoreLocationProps {
    center: LatLngLiteralProps;
    locale: Locale;
    onLatLngChanged: (center: LatLngLiteralProps, bounds: LatLngLiteralProps) => void;
    currentCategory?: CategoryProps;
    parentCategory?: CategoryProps;
    store?: SingleStoreProps;
    onBoundsChanged: (bounds: LatLngLiteralProps) => void;
    radius: number;
}

type StoreProps = {
    geocodedResults: GeocodedResultsProps;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
    onClick: (event: SyntheticEvent) => void;
    isPostcodeFilled: boolean;
    hasError: boolean;
};

type CategorySlug = {
    slug: string;
};

interface Props {
    slug: string;
    context: string;
    store?: SingleStoreProps;
    currentCategory?: CategoryProps;
    parentCategory?: CategoryProps;
}

type StoreLocationProps = StoreProps & InStoreLocationProps;
type CategoryLocationProps = CategorySlug & InStoreLocationProps;

const StoreLocations: React.FC<StoreLocationProps> = ({
    center,
    onLatLngChanged,
    store,
    locale,
    onChange,
    onClick,
    geocodedResults,
    onBoundsChanged,
    radius,
    isPostcodeFilled,
    hasError,
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const locations: Array<LocationProps> = useStoreLocations(locale, center, store, radius);
    const hasLocations = useMemo(() => locations.length ?? false, [locations]);
    const { t } = useTranslation();

    useEffect(() => {
        if (hasLocations) {
            setIsLoading(false);
        }
    }, [hasLocations]);

    const inStoreLocations = isLoading ? (
        <div className={styles.instoreListContainer}>
            <Loader />
        </div>
    ) : hasLocations ? (
        <InStoreList locations={locations} />
    ) : (
        <InStoreNoLocations />
    );

    return (
        <>
            <div className={styles.instoreHeaderContainer}>
                <div className={styles.instoreHeaderNameContainer}>
                    <h1 className={styles.instoreHeader}>{store.name}</h1>{" "}
                    {hasLocations ? (
                        <p className={styles.instoreLocationsCount}>
                            {locations.length}{" "}
                            {locations.length > 1
                                ? t("inStore.storesHeader.plural")
                                : t("inStore.storesHeader.single")}
                        </p>
                    ) : null}
                </div>
                <div className={styles.instoreSearchContainer}>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                        }}
                    >
                        <Input
                            placeholder={t("inStore.postcode.input.label")}
                            size={"Medium"}
                            onChange={onChange}
                            errorText={t("inStore.postcode.error")}
                            // GB and CA postcodes are alphanumeric so this uses
                            // text instead of number
                            kind={"text"}
                            icon={"Location"}
                            error={hasError}
                            iconPosition={"Left"}
                        />

                        <Button
                            type={"submit"}
                            disabled={!isPostcodeFilled}
                            onClick={onClick}
                            kind={"Primary"}
                            size={"Regular"}
                        >
                            {t("inStore.postcode.cta.label")}
                        </Button>
                    </form>
                </div>
            </div>
            <div className={styles.instoreContainer}>
                <Grid fullWidth fullHeight>
                    {inStoreLocations}
                    <InStoreMap
                        locations={locations}
                        onLatLngChanged={onLatLngChanged}
                        geocodedResults={geocodedResults}
                        onBoundsChanged={onBoundsChanged}
                    />
                </Grid>
            </div>
        </>
    );
};

const CategoryLocations: React.FC<CategoryLocationProps> = ({
    locale,
    center,
    slug,
    onLatLngChanged,
    currentCategory,
    parentCategory,
    onBoundsChanged,
    radius,
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [hasLocations, setHasLocations] = useState<boolean>(false);
    const locations = useCategoryLocations(slug, locale, center, radius);
    const { t } = useTranslation();
    const isArcade = useIsArcade();

    useEffect(() => {
        const _hasLocations = locations?.length > 0;
        setHasLocations(_hasLocations);

        if (_hasLocations) {
            setIsLoading(false);
        }
    }, [locations]);

    const inStoresList = isLoading ? (
        <div className={styles.instoreListContainer}>
            <Loader />
        </div>
    ) : hasLocations ? (
        <InStoreList locations={locations} />
    ) : (
        <InStoreNoLocations />
    );

    return (
        <>
            <div className={styles.instoreCategoryHeaderContainer}>
                <BreadCrumbs
                    parentLink={parentCategory}
                    currentLink={currentCategory}
                    pageType={PAGE_TYPES.IN_STORE}
                />
                {isArcade ? (
                    <h5 className={styles.instoreHeader}>
                        {currentCategory.name} {t("inStore.categoryHeader")}
                    </h5>
                ) : (
                    <h1 className={styles.instoreHeader}>
                        {currentCategory.name} {t("inStore.categoryHeader")}
                    </h1>
                )}
            </div>
            <div className={styles.instoreContainer}>
                <Grid fullWidth fullHeight>
                    {inStoresList}
                    <InStoreMap
                        locations={locations}
                        onLatLngChanged={onLatLngChanged}
                        onBoundsChanged={onBoundsChanged}
                    />
                </Grid>
            </div>
        </>
    );
};

export const InStoreContainer: React.FC<Props> = (props: Props) => {
    const { slug, context, store, currentCategory, parentCategory } = props;
    const { lat, lng } = usePosition(true);
    const [postcode, setPostcode] = useState<string>("");
    const [geocodedResults, setGeocodedResults] = useState<GeocodedResultsProps>(null);
    const [hasError, setHasError] = useState(false);
    const [center, setCenter] = useState<LatLngLiteralProps>({
        lat: lat,
        lng: lng,
    });
    const [bounds, setBounds] = useState<LatLngLiteralProps>({
        lat: 0,
        lng: 0,
    });
    const locale = useCurrentLocale();
    const country = localeCountryCodeMap[locale];
    const radius = distanceBetweenPositions(bounds, center);
    const isCategoryContext = context === CATEGORY_LOCATIONS_CONTEXT;
    const isPostcodeFilled = !!postcode.length;

    useEffect(() => {
        if (geocodedResults) {
            setCenter({
                lat: geocodedResults.location.lat,
                lng: geocodedResults.location.lng,
            });
        }
    }, [geocodedResults]);

    useEffect(() => {
        setCenter({ lat, lng });
    }, [lat, lng]);

    const onLatLngChanged = (center, bounds) => {
        setBounds({
            lat: bounds.lat(),
            lng: bounds.lng(),
        });
        setCenter({
            lat: center.lat(),
            lng: center.lng(),
        });
    };

    const onBoundsChanged = (bounds) => {
        setBounds({
            lat: bounds.lat(),
            lng: bounds.lng(),
        });
    };

    const onChange = async (e) => {
        setPostcode(e.target.value);
    };

    const onKeyDown = async (e) => {
        if (e.key === "Enter" && isPostcodeFilled) await loadGeocodedResults(country, postcode);
    };

    const onClick = async () => {
        await loadGeocodedResults(country, postcode);
    };

    const loadGeocodedResults = async (country: string, postcode: string) => {
        const results = await getGeocodedLocations(country, postcode);
        if (!results) {
            setHasError(true);
            return;
        } else {
            setHasError(false);
            setGeocodedResults(results);
        }
    };

    return (
        <>
            {isCategoryContext ? (
                <CategoryLocations
                    center={center}
                    slug={slug}
                    onLatLngChanged={onLatLngChanged}
                    currentCategory={currentCategory}
                    parentCategory={parentCategory}
                    locale={locale}
                    onBoundsChanged={onBoundsChanged}
                    radius={radius}
                />
            ) : (
                <StoreLocations
                    center={center}
                    onLatLngChanged={onLatLngChanged}
                    store={store}
                    locale={locale}
                    onClick={onClick}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    geocodedResults={geocodedResults}
                    onBoundsChanged={onBoundsChanged}
                    radius={radius}
                    isPostcodeFilled={isPostcodeFilled}
                    hasError={hasError}
                />
            )}
        </>
    );
};
